// json import removed - return plain objects in React Router v7
import type { MetaFunction } from 'react-router';

export const handle = {
  title: 'Privacy Policy - Pavone',
  description: 'Learn about how we collect, use, and protect your personal data.',
};

export const meta: MetaFunction = () => {
  // TODO: Add your Facebook App ID from https://developers.facebook.com/apps/
  //const fbAppId = process.env.FACEBOOK_APP_ID || ''; 751017064296932
  const fbAppId = '751017064296932';
  return [
    { title: 'Privacy Policy - Pavone' },
    { name: 'description', content: 'Learn about how we collect, use, and protect your personal data.' },
    { property: 'og:title', content: 'Privacy Policy - Pavone' },
    { property: 'og:description', content: 'Learn about how we collect, use, and protect your personal data in compliance with GDPR.' },
    { property: 'og:type', content: 'website' },
    { property: 'og:url', content: 'https://pavone.ai/privacy' },
    { property: 'og:image', content: 'https://pavone.ai/images/pavone-full.png' },
    { property: 'og:image:alt', content: 'Pavone - AI Job Interview Practice' },
    { property: 'og:site_name', content: 'Pavone' },
    ...(fbAppId ? [{ property: 'fb:app_id', content: fbAppId }] : []),
    { name: 'twitter:card', content: 'summary_large_image' },
    { name: 'twitter:title', content: 'Privacy Policy - Pavone' },
    { name: 'twitter:description', content: 'Learn about how we collect, use, and protect your personal data.' },
    { name: 'twitter:image', content: 'https://pavone.ai/images/pavone-full.png' },
  ];
};

export default function Privacy() {
  return (
    <div className="min-h-screen bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-4xl mx-auto">
        <div className="bg-white shadow-lg rounded-lg p-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-8">
            Privacy Policy
          </h1>
          <p className="text-sm text-gray-600 mb-8">Last updated: 8.10.2025</p>

          <div className="space-y-8">
            <section>
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">
                1. Introduction
              </h2>
              <p className="text-gray-700 mb-4">
                Pavone ("we," "our," or "us") is committed to protecting your
                privacy. This Privacy Policy explains how we collect, use,
                disclose, and safeguard your information when you use our video
                analysis and speech improvement platform.
              </p>
              <p className="text-gray-700">
                This policy complies with the General Data Protection Regulation
                (GDPR) and other applicable privacy laws.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">
                2. Information We Collect
              </h2>

              <h3 className="text-xl font-medium text-gray-800 mb-3">
                2.1 Personal Information
              </h3>
              <ul className="list-disc pl-6 text-gray-700 mb-4 space-y-2">
                <li>
                  <strong>Account Information:</strong> Name, email address,
                  authentication credentials
                </li>
                <li>
                  <strong>Payment Information:</strong> Processed securely
                  through Stripe (we don't store payment details)
                </li>
                <li>
                  <strong>Profile Data:</strong> Subscription plan, practice
                  history, preferences
                </li>
              </ul>

              <h3 className="text-xl font-medium text-gray-800 mb-3">
                2.2 Video and Audio Data
              </h3>
              <ul className="list-disc pl-6 text-gray-700 mb-4 space-y-2">
                <li>
                  <strong>Video Recordings:</strong> Practice sessions you
                  upload for analysis
                </li>
                <li>
                  <strong>Transcriptions:</strong> Audio-to-text conversions of
                  your recordings
                </li>
                <li>
                  <strong>Analysis Results:</strong> AI-generated feedback,
                  scores, and recommendations
                </li>
              </ul>

              <h3 className="text-xl font-medium text-gray-800 mb-3">
                2.3 Technical Information
              </h3>
              <ul className="list-disc pl-6 text-gray-700 space-y-2">
                <li>
                  <strong>Usage Data:</strong> How you interact with our
                  platform
                </li>
                <li>
                  <strong>Device Information:</strong> Browser type, operating
                  system, IP address
                </li>
                <li>
                  <strong>Log Data:</strong> Error logs, performance metrics,
                  security events
                </li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">
                3. Legal Basis for Processing (GDPR)
              </h2>
              <ul className="list-disc pl-6 text-gray-700 space-y-2">
                <li>
                  <strong>Contract Performance:</strong> Processing necessary to
                  provide our services
                </li>
                <li>
                  <strong>Legitimate Interest:</strong> Improving our platform,
                  security, and user experience
                </li>
                <li>
                  <strong>Consent:</strong> Where you've given explicit consent
                  for specific processing
                </li>
                <li>
                  <strong>Legal Obligation:</strong> Compliance with applicable
                  laws and regulations
                </li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">
                4. How We Use Your Information
              </h2>
              <ul className="list-disc pl-6 text-gray-700 space-y-2">
                <li>Provide video analysis and speech improvement services</li>
                <li>Generate personalized feedback and recommendations</li>
                <li>Process payments and manage subscriptions</li>
                <li>
                  Communicate with you about your account and our services
                </li>
                <li>Improve our platform and develop new features</li>
                <li>Ensure security and prevent fraud</li>
                <li>Comply with legal obligations</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">
                5. Data Sharing and Disclosure
              </h2>
              <p className="text-gray-700 mb-4">
                We do not sell your personal data. We may share information
                with:
              </p>
              <ul className="list-disc pl-6 text-gray-700 space-y-2">
                <li>
                  <strong>Service Providers:</strong> Clerk (authentication),
                  Stripe (payments), AssemblyAI (transcription), Cloudflare R2
                  (storage)
                </li>
                <li>
                  <strong>Legal Requirements:</strong> When required by law or
                  legal process
                </li>
                <li>
                  <strong>Business Transfers:</strong> In connection with
                  mergers, acquisitions, or asset sales
                </li>
                <li>
                  <strong>Consent:</strong> When you've given explicit consent
                </li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">
                6. Data Retention
              </h2>
              <ul className="list-disc pl-6 text-gray-700 space-y-2">
                <li>
                  <strong>Account Data:</strong> Retained while your account is
                  active and for 30 days after deletion
                </li>
                <li>
                  <strong>Video Recordings:</strong> Retained for 90 days or
                  until you delete them
                </li>
                <li>
                  <strong>Analysis Data:</strong> Retained for 1 year to improve
                  our services
                </li>
                <li>
                  <strong>Log Data:</strong> Retained for 30 days for security
                  and debugging purposes
                </li>
                <li>
                  <strong>Temporary Files:</strong> Automatically deleted within
                  24 hours
                </li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">
                7. Your Rights (GDPR)
              </h2>
              <p className="text-gray-700 mb-4">
                If you're in the EU, you have the following rights:
              </p>
              <ul className="list-disc pl-6 text-gray-700 space-y-2">
                <li>
                  <strong>Access:</strong> Request a copy of your personal data
                </li>
                <li>
                  <strong>Rectification:</strong> Correct inaccurate or
                  incomplete data
                </li>
                <li>
                  <strong>Erasure:</strong> Request deletion of your personal
                  data ("right to be forgotten")
                </li>
                <li>
                  <strong>Portability:</strong> Receive your data in a
                  machine-readable format
                </li>
                <li>
                  <strong>Restriction:</strong> Limit how we process your data
                </li>
                <li>
                  <strong>Objection:</strong> Object to processing based on
                  legitimate interests
                </li>
                <li>
                  <strong>Withdraw Consent:</strong> Withdraw consent where
                  processing is based on consent
                </li>
              </ul>
              <p className="text-gray-700 mt-4">
                To exercise these rights, contact us at privacy@speak.com or use
                the data management tools in your account settings.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">
                8. Data Security
              </h2>
              <ul className="list-disc pl-6 text-gray-700 space-y-2">
                <li>All data encrypted in transit using TLS 1.3</li>
                <li>Data encrypted at rest in our storage systems</li>
                <li>Access controls and authentication for all systems</li>
                <li>Regular security audits and monitoring</li>
                <li>Incident response procedures for data breaches</li>
                <li>Employee training on data protection</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">
                9. International Data Transfers
              </h2>
              <p className="text-gray-700">
                Your data may be processed in countries outside your residence.
                We ensure appropriate safeguards are in place, including
                Standard Contractual Clauses approved by the European
                Commission.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">
                10. Children's Privacy
              </h2>
              <p className="text-gray-700">
                Our service is not intended for children under 16. We do not
                knowingly collect personal information from children under 16.
                If we become aware of such collection, we will delete the
                information immediately.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">
                11. Data Breach Notification
              </h2>
              <p className="text-gray-700">
                In the event of a data breach that poses a risk to your rights
                and freedoms, we will notify the relevant supervisory authority
                within 72 hours and inform affected users without undue delay.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">
                12. Data Deletion & Contact
              </h2>
              <div className="text-gray-700">
                <p className="mb-3">
                  To request deletion of your personal data, please contact us via email below.
                </p>
                <p className="mb-2">
                  <strong>General Inquiries:</strong> info@pavone.ai
                </p>
              </div>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">
                13. Changes to This Policy
              </h2>
              <p className="text-gray-700">
                We may update this Privacy Policy from time to time. We will
                notify you of any material changes by posting the new policy on
                this page and updating the "Last updated" date. Continued use of
                our service after changes constitutes acceptance of the updated
                policy.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">
                14. Supervisory Authority
              </h2>
              <p className="text-gray-700">
                If you're in the EU and have concerns about our data processing,
                you have the right to lodge a complaint with your local data
                protection authority.
              </p>
            </section>
          </div>
        </div>
      </div>
    </div>
  );
}
